import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api';

/**
 * Integration-secret management (Settings → Integrations "Secrets" sub-section).
 *
 * Talks to the config domain's admin-only endpoints:
 *   - GET  /api/config/secrets/status        → { [key]: boolean }  (configured / not)
 *   - POST /api/config/secrets/{secretKey}    → { success: true }   (set SecureString)
 *
 * The secret VALUE is never returned by the API and is never cached here — only the boolean
 * "configured" status. Both endpoints are admin-only; the backend re-enforces RBAC.
 */

/** The nine configurable integration secrets (mirrors packages/config/handlers/secrets-status.ts). */
export type SecretKey =
  | 'github_token'
  | 'slack_bot_token'
  | 'jira_api_token'
  | 'jira_base_url'
  | 'jira_web_base_url'
  | 'jira_email'
  | 'clockify_api_token'
  | 'clockify_workspace_id'
  | 'avoma_api_key';

export type SecretsStatus = Record<SecretKey, boolean>;

/** Fetch which secrets are configured. Admin-only — disabled unless `enabled` is true. */
export function useSecretsStatus(enabled: boolean) {
  const client = useApiClient();

  return useQuery({
    queryKey: ['secrets-status'],
    enabled,
    queryFn: async (): Promise<SecretsStatus> => {
      const response = await client.get<SecretsStatus>('/api/config/secrets/status', {
        _suppressErrorToast: true,
      });
      return response.data;
    },
  });
}

/** Set (create/overwrite) a single integration secret. Invalidates the status query on success. */
export function useSetSecret() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: SecretKey; value: string }) => {
      const response = await client.post<{ success: boolean }>(
        `/api/config/secrets/${key}`,
        { value },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['secrets-status'] });
    },
  });
}
